import React, { useMemo, useState, useCallback, useRef } from 'react';
import SEO from '../components/SEO';
import styles from '../css/modules/Kwestionariusz.module.css';
import { SECTIONS, TOTAL_QUESTIONS, qid } from '../data/kwestionariusz';
import { FIRMA } from '../data/firma';
import { track } from '../lib/analytics';

// Ta strona jest narzedziem, nie strona ofertowa: caly ruch komercyjny na
// fraze "diagnoza sensoryczna Olsztyn" nalezy do /diagnoza-sensoryczna-olsztyn/.
// Dlatego noindex,follow - formularz nie ma konkurowac z landingiem
// ani zbierac wejsc z wyszukiwarki (spec sekcje 4 i 13).
const PATH = '/kwestionariusz-sensomotoryczny/';

const INSTITUTIONS = ['żłobek', 'przedszkole', 'szkoła', 'dom (nie uczęszcza)'];

export default function Kwestionariusz() {
  const [meta, setMeta] = useState({
    child_name: '',
    child_birthdate: '',
    sex: '',
    institution: '',
    born_on_time: '',
    delivery: '',
    questionnaire_date: new Date().toISOString().slice(0, 10),
  });
  const [contact, setContact] = useState({
    parent_name: '',
    email: '',
    phone: '',
    main_concern: '',
  });
  const [answers, setAnswers] = useState({});
  const [consent, setConsent] = useState(false);
  const [status, setStatus] = useState('idle'); // idle | sending | done | error
  const [errorMsg, setErrorMsg] = useState('');

  const answeredCount = useMemo(() => Object.keys(answers).length, [answers]);
  const progress = Math.round((answeredCount / TOTAL_QUESTIONS) * 100);

  // form_start wysylamy raz, przy pierwszej realnej interakcji z formularzem.
  const startedRef = useRef(false);
  const markStart = useCallback(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    track('form_start', { form_id: 'kwestionariusz-sensomotoryczny' });
  }, []);

  const setAnswer = useCallback(
    (id, val) => {
      markStart();
      setAnswers((prev) => ({ ...prev, [id]: val }));
    },
    [markStart]
  );

  const buildPayload = () => {
    const sections = [];
    SECTIONS.forEach((s) => {
      s.groups.forEach((g, gIdx) => {
        const items = g.questions.map((q, qIdx) => ({
          q,
          a: answers[qid(s.key, gIdx, qIdx)] || '',
        }));
        const title = g.subtitle ? `${s.title} - ${g.subtitle}` : s.title;
        sections.push({ title, items });
      });
    });
    return { meta, contact, consent, sections };
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg('');
    const fail = (msg, powod) => {
      setErrorMsg(msg);
      track('form_error', { form_id: 'kwestionariusz-sensomotoryczny', error_reason: powod });
    };
    if (!consent) {
      return fail('Aby wysłać kwestionariusz, zaznacz zgodę na przetwarzanie danych (RODO).', 'brak_zgody');
    }
    if (!contact.email && !contact.phone) {
      return fail('Podaj e-mail lub numer telefonu, abyśmy mogli się z Tobą skontaktować.', 'brak_kontaktu');
    }
    if (answeredCount < 1) {
      return fail(
        'Zaznacz odpowiedź (Tak/Nie) przy co najmniej jednym pytaniu, aby wysłać kwestionariusz.',
        'brak_odpowiedzi'
      );
    }
    setStatus('sending');
    try {
      const res = await fetch('/api/diagnoza/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(buildPayload()),
      });
      const data = await res.json();
      if (res.ok && data.ok) {
        setStatus('done');
        track('form_submit', { form_id: 'kwestionariusz-sensomotoryczny', answered: answeredCount });
        track('booking_complete', { method: 'kwestionariusz' });
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        setStatus('error');
        fail(data.error || 'Nie udało się wysłać kwestionariusza. Spróbuj ponownie.', 'odrzucone_przez_serwer');
      }
    } catch (err) {
      setStatus('error');
      fail('Błąd połączenia. Sprawdź internet i spróbuj ponownie.', 'blad_polaczenia');
    }
  };

  if (status === 'done') {
    return (
      <section className={styles.wrapper}>
        <SEO
          title="Kwestionariusz wysłany | Sensorini Olsztyn"
          description="Dziękujemy za wypełnienie kwestionariusza sensomotorycznego. Odezwiemy się w sprawie terminu diagnozy."
          pathname={PATH}
          type="website"
          noindex
        />
        <div className={styles.card}>
          <div className={styles.doneBox}>
            <h1>Dziękujemy! 🎉</h1>
            <p>
              Twój kwestionariusz sensomotoryczny został wysłany do gabinetu Sensorini w Olsztynie.
              Skontaktujemy się z Tobą, aby ustalić termin <strong>diagnozy integracji sensorycznej</strong>.
            </p>
            <p className={styles.doneContact}>
              Potrzebujesz szybkiego kontaktu? Zadzwoń:{' '}
              <a href={`tel:${FIRMA.telefonE164}`}>{FIRMA.telefon}</a>
            </p>
            <a className={styles.primaryBtn} href="/">Wróć na stronę główną</a>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className={styles.wrapper}>
      <SEO
        title="Kwestionariusz sensomotoryczny dziecka | Sensorini Olsztyn"
        description="Wypełnij bezpłatny kwestionariusz sensomotoryczny przed diagnozą integracji sensorycznej w gabinecie Sensorini w Olsztynie."
        pathname={PATH}
        type="website"
        noindex
      />

      <div className={styles.card}>
        <header className={styles.hero}>
          <h1 className={styles.title}>Kwestionariusz sensomotoryczny dziecka</h1>
          <p className={styles.lead}>
            To formularz przygotowujący do wizyty, a nie sama diagnoza. Wypełnij go przed spotkaniem
            w gabinecie <strong>{FIRMA.nazwaKrotka}</strong> ({FIRMA.ulica}, {FIRMA.miasto}) - na jego
            podstawie przygotuję się do wywiadu i odezwę się, żeby ustalić termin. Jeśli szukasz
            informacji o samym badaniu, zajrzyj na stronę{' '}
            <a href="/diagnoza-sensoryczna-olsztyn/">diagnozy integracji sensorycznej w Olsztynie</a>.
          </p>
          <ul className={styles.heroPoints}>
            <li>✔ Wypełnienie zajmuje ok. 10-15 minut i jest bezpłatne.</li>
            <li>✔ To wstępny wywiad, a nie gotowa diagnoza - pełną diagnozę przeprowadzam osobiście.</li>
            <li>✔ Odpowiedzi trafiają bezpiecznie do gabinetu i pomagają lepiej opisać trudności dziecka.</li>
          </ul>
        </header>

        <form className={styles.form} onSubmit={onSubmit}>
          {/* --- DANE DZIECKA --- */}
          <fieldset className={styles.metaBox}>
            <legend>Dane dziecka</legend>
            <div className={styles.grid}>
              <label className={styles.field}>
                <span>Imię i nazwisko dziecka</span>
                <input value={meta.child_name}
                  onChange={(e) => setMeta({ ...meta, child_name: e.target.value })} />
              </label>
              <label className={styles.field}>
                <span>Data urodzenia dziecka</span>
                <input type="date" value={meta.child_birthdate}
                  onChange={(e) => setMeta({ ...meta, child_birthdate: e.target.value })} />
              </label>
              <label className={styles.field}>
                <span>Płeć dziecka</span>
                <select value={meta.sex}
                  onChange={(e) => setMeta({ ...meta, sex: e.target.value })}>
                  <option value="">- wybierz -</option>
                  <option value="chlopiec">chłopiec</option>
                  <option value="dziewczynka">dziewczynka</option>
                </select>
              </label>
              <label className={styles.field}>
                <span>Dziecko uczęszcza do</span>
                <select value={meta.institution}
                  onChange={(e) => setMeta({ ...meta, institution: e.target.value })}>
                  <option value="">- wybierz -</option>
                  {INSTITUTIONS.map((i) => <option key={i} value={i}>{i}</option>)}
                </select>
              </label>
              <label className={styles.field}>
                <span>Dziecko urodziło się o czasie?</span>
                <select value={meta.born_on_time}
                  onChange={(e) => setMeta({ ...meta, born_on_time: e.target.value })}>
                  <option value="">- wybierz -</option>
                  <option value="Tak">Tak</option>
                  <option value="Nie">Nie</option>
                </select>
              </label>
              <label className={styles.field}>
                <span>Rodzaj porodu</span>
                <select value={meta.delivery}
                  onChange={(e) => setMeta({ ...meta, delivery: e.target.value })}>
                  <option value="">- wybierz -</option>
                  <option value="siłami natury">siłami natury</option>
                  <option value="cesarskie cięcie">cesarskie cięcie</option>
                </select>
              </label>
              <label className={styles.field}>
                <span>Data wypełnienia kwestionariusza</span>
                <input type="date" value={meta.questionnaire_date}
                  onChange={(e) => setMeta({ ...meta, questionnaire_date: e.target.value })} />
              </label>
            </div>
          </fieldset>

          {/* --- SEKCJE ZMYSŁOWE --- */}
          {SECTIONS.map((s) => (
            <div key={s.key} className={styles.section}>
              <h2 className={styles.sectionTitle}>{s.title}</h2>
              {s.groups.map((g, gIdx) => (
                <div key={gIdx} className={styles.group}>
                  {g.subtitle && <h3 className={styles.groupTitle}>{g.subtitle}</h3>}
                  <ul className={styles.questions}>
                    {g.questions.map((q, qIdx) => {
                      const id = qid(s.key, gIdx, qIdx);
                      return (
                        <li key={id} className={styles.questionItem}>
                          <span className={styles.questionText}>{q}</span>
                          <div className={styles.radios}>
                            {['Tak', 'Nie'].map((val) => (
                              <label key={val}
                                className={`${styles.radioPill} ${answers[id] === val ? styles.radioActive : ''}`}>
                                <input type="radio" name={id} value={val}
                                  checked={answers[id] === val}
                                  onChange={() => setAnswer(id, val)} />
                                <span>{val}</span>
                              </label>
                            ))}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </div>
          ))}

          {/* --- KONTAKT + ZGODA --- */}
          <fieldset className={styles.metaBox}>
            <legend>Kontakt i zgoda</legend>
            <div className={styles.grid}>
              <label className={styles.field}>
                <span>Imię i nazwisko rodzica / opiekuna</span>
                <input value={contact.parent_name}
                  onChange={(e) => setContact({ ...contact, parent_name: e.target.value })} />
              </label>
              <label className={styles.field}>
                <span>Adres e-mail</span>
                <input type="email" value={contact.email}
                  onChange={(e) => setContact({ ...contact, email: e.target.value })}
                  placeholder="np. rodzic@example.com" />
              </label>
              <label className={styles.field}>
                <span>Numer telefonu</span>
                <input type="tel" value={contact.phone}
                  onChange={(e) => setContact({ ...contact, phone: e.target.value })}
                  placeholder="np. 600 100 200" />
              </label>
            </div>
            <label className={`${styles.field} ${styles.fieldFull}`}>
              <span>Co najbardziej niepokoi Państwa w codziennym funkcjonowaniu dziecka?</span>
              <textarea rows={4} value={contact.main_concern}
                onChange={(e) => setContact({ ...contact, main_concern: e.target.value })}
                placeholder="Opisz krótko główne trudności, które chcecie skonsultować." />
            </label>

            <label className={styles.consent}>
              <input type="checkbox" checked={consent}
                onChange={(e) => setConsent(e.target.checked)} />
              <span>
                Wyrażam zgodę na przetwarzanie podanych danych osobowych (w tym danych dziecka)
                przez gabinet Sensorini w Olsztynie w celu kontaktu i przygotowania diagnozy
                integracji sensorycznej. Szczegóły w{' '}
                <a href="/polityka-prywatnosci/" target="_blank" rel="noopener noreferrer">Polityce prywatności</a>.
              </span>
            </label>
          </fieldset>

          {/* --- PASEK POSTĘPU + WYSYŁKA --- */}
          <div className={styles.submitBar}>
            <div className={styles.progressWrap}>
              <div className={styles.progressTrack}>
                <div className={styles.progressBar} style={{ width: `${progress}%` }} />
              </div>
              <span className={styles.progressLabel}>
                Uzupełniono {answeredCount} / {TOTAL_QUESTIONS} pytań
              </span>
            </div>
            {errorMsg && <p className={styles.error}>{errorMsg}</p>}
            <button type="submit" className={styles.primaryBtn} disabled={status === 'sending'}>
              {status === 'sending' ? 'Wysyłanie…' : 'Wyślij kwestionariusz do gabinetu'}
            </button>
            <p className={styles.privacyNote}>
              Nie musisz odpowiadać na wszystkie pytania - wyślij tyle, ile potrafisz ocenić.
            </p>
          </div>
        </form>

        {/* Krotki blok pomocniczy. Tresc komercyjna i lokalna nalezy do
            /diagnoza-sensoryczna-olsztyn/ - tutaj tylko odsylacze. */}
        <aside className={styles.localSeo}>
          <h2>Wolisz porozmawiać?</h2>
          <p>
            Formularz nie jest obowiązkowy - termin możesz ustalić także telefonicznie. Opis
            przebiegu badania, cena i informacje o dojeździe znajdują się na stronie diagnozy.
          </p>
          <div className={styles.contactRow}>
            <a className={styles.primaryBtn} href={`tel:${FIRMA.telefonE164}`}>
              Zadzwoń: {FIRMA.telefon}
            </a>
            <a className={styles.ghostBtn} href="/diagnoza-sensoryczna-olsztyn/">
              Jak wygląda diagnoza
            </a>
            <a className={styles.ghostBtn} href="/kontakt/">
              Dane kontaktowe i dojazd
            </a>
          </div>
        </aside>
      </div>
    </section>
  );
}
